import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Box, Button, TextField, Typography, Paper, Alert, InputAdornment, Avatar, CircularProgress } from "@mui/material"
import EmailIcon from "@mui/icons-material/Email"
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid"
import LockIcon from "@mui/icons-material/Lock"
import { useAuth } from "../context/AuthContext"

const Login = () => {
  const [step, setStep] = useState(1) // 1: Email Input, 2: Choose Method, 3: Enter SMS Code, 4: Enter Email Code
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState(null)
  const [smsCode, setSmsCode] = useState("")
  const [emailCode, setEmailCode] = useState("")
  const [validationToken, setValidationToken] = useState("")
  const [localError, setLocalError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [cooldown, setCooldown] = useState(0) // Seconds remaining for email cooldown

  const navigate = useNavigate()
  const {
    user, loading, error,
    checkEligibility, sendLoginLink, verifyEmailOtp,
    setupRecaptcha, sendSmsCode, verifySmsCode
  } = useAuth()

  // If user is already logged in, redirect to home
  useEffect(() => {
    if (user || localStorage.getItem("token")) {
      navigate("/")
    }
  }, [user, navigate])

  // Handle email cooldown timer
  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleCheckEmail = async (e) => {
    e.preventDefault()
    setLocalError("")
    try {
      // Trigger invisible reCAPTCHA before checking Mailchimp (Protect API)
      setupRecaptcha("recaptcha-container")
      
      const data = await checkEligibility(email)
      setPhone(data.phone)
      setStep(2) // Move to method selection
    } catch (err) {
      // Error handled by AuthContext or we can set local
      setLocalError("This email is not authorized.")
    }
  }

  const handleSendEmailLink = async () => {
    setLocalError("")
    setSuccessMessage("")
    try {
      // Trigger invisible reCAPTCHA for email too (Bot protection)
      setupRecaptcha("recaptcha-container")
      
      const token = await sendLoginLink(email)
      setValidationToken(token)
      setStep(4) // Move to enter code screen
      setSuccessMessage("Login code sent to your email!")
    } catch (err) {
      const isRateLimit = err.response?.status === 429;
      const msg = err.response?.data?.error || "Failed to send login code.";
      setLocalError(msg)
      
      if (isRateLimit) {
        setCooldown(60);
      }
    }
  }

  const handleVerifyEmailOtp = async (e) => {
    e.preventDefault()
    setLocalError("")
    try {
      await verifyEmailOtp(email, emailCode, validationToken)
    } catch (err) {
      // The error message from WordPress (like "Please wait 60 seconds") 
      // is captured by AuthContext and passed here.
      setLocalError(err.message || "Invalid login code.")
    } finally {
    }
  }

  const handleSendSms = async () => {
    setLocalError("")
    setSuccessMessage("")
    try {
      const appVerifier = setupRecaptcha("recaptcha-container")
      // Ensure phone is formatted properly (+1 for US)
      let formattedPhone = phone.replace(/\D/g, "")
      if (formattedPhone.length === 10) formattedPhone = `+1${formattedPhone}`
      else if (!formattedPhone.startsWith("+")) formattedPhone = `+${formattedPhone}`

      await sendSmsCode(formattedPhone, appVerifier)
      setStep(3) // Move to enter SMS code
      setSuccessMessage("SMS code sent!")
    } catch (err) {
      console.error("SMS Error:", err)
      setLocalError(`Failed to send SMS: ${err.message || "Unknown error"}`)
    }
  }

  const handleVerifySms = async (e) => {
    e.preventDefault()
    setLocalError("")
    try {
      await verifySmsCode(smsCode)
      // On success, AuthContext user state updates and useEffect redirects to /
    } catch (err) {
      setLocalError("Invalid SMS code.")
    }
  }

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #ccffff 0%, #ffffcc 50%, #ffccff 100%)",
        padding: { xs: 2, sm: 4 }
      }}
    >
      <Paper
        elevation={12}
        sx={{
          p: 4,
          width: "auto",
          minWidth: { xs: "280px", sm: "400px" },
          maxWidth: "90%",
          textAlign: "center"
        }}
      >
        <Avatar
          sx={{
            margin: "0 auto",
            backgroundColor: "primary.main",
            width: 56,
            height: 56,
            mb: 2
          }}
        >
          {step === 3 ? <LockIcon sx={{ fontSize: 32 }} /> : <EmailIcon sx={{ fontSize: 32 }} />}
        </Avatar>

        <Typography variant="h5" gutterBottom>
          Directory Login
        </Typography>

        {loading ? (
          <Box sx={{ py: 4 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Processing...
            </Typography>
          </Box>
        ) : (
          <React.Fragment>
            {(error || localError) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {localError || error}
              </Alert>
            )}

            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}

            {step === 1 && (
              <form onSubmit={handleCheckEmail}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Enter your email address to continue.
                </Typography>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={loading}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color={loading ? "disabled" : "primary"} />
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading || !email}
                  sx={{ mt: 2 }}
                >
                  {loading ? "Checking..." : "Continue"}
                </Button>
              </form>
            )}

            {step === 2 && (
              <Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  How would you like to receive your login code?
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSendEmailLink}
                    disabled={loading || cooldown > 0}
                    startIcon={<EmailIcon />}
                  >
                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Send Email Code"}
                  </Button>
                  {phone && (
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      onClick={handleSendSms}
                      disabled={loading}
                      startIcon={<PhoneAndroidIcon />}
                    >
                      Text Code to Phone
                    </Button>
                  )}
                </Box>
                <Button
                  sx={{ mt: 2 }}
                  onClick={() => setStep(1)}
                  disabled={loading}
                >
                  Back
                </Button>
              </Box>
            )}

            {step === 3 && (
              <form onSubmit={handleVerifySms}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Enter the SMS code sent to your phone.
                </Typography>
                <TextField
                  fullWidth
                  label="SMS Code"
                  type="text"
                  value={smsCode}
                  onChange={e => setSmsCode(e.target.value)}
                  disabled={loading}
                  margin="normal"
                  required
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading || !smsCode}
                  sx={{ mt: 2 }}
                >
                  {loading ? "Verifying..." : "Verify Code"}
                </Button>
                <Button
                  sx={{ mt: 2 }}
                  onClick={() => setStep(2)}
                  disabled={loading}
                >
                  Back
                </Button>
              </form>
            )}

            {step === 4 && (
              <form onSubmit={handleVerifyEmailOtp}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Enter the 6-digit code sent to <strong>{email}</strong>.
                </Typography>
                <TextField
                  fullWidth
                  label="Email Login Code"
                  type="text"
                  value={emailCode}
                  onChange={e => setEmailCode(e.target.value)}
                  disabled={loading}
                  margin="normal"
                  required
                  placeholder="000 000"
                  inputProps={{ style: { textAlign: 'center', letterSpacing: '4px', fontSize: '20px' } }}
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading || !emailCode}
                  sx={{ mt: 2 }}
                >
                  {loading ? "Verifying..." : "Verify Code"}
                </Button>
                <Button
                  sx={{ mt: 2 }}
                  onClick={() => setStep(2)}
                  disabled={loading}
                >
                  Back
                </Button>
              </form>
            )}
          </React.Fragment>
        )}
        <div id="recaptcha-container"></div>
      </Paper>
    </Box>
  )
}

export default Login
